/*
  MASTER — oferta de rescate.

  Tres estados que ANTES estaban fundidos en uno, y de ahí venía el 5% fantasma:

    1. offerEligible          ¿procede ofrecerlo ahora?
    2. offerShown             ¿ya se enseñó en esta sesión?   (sólo frecuencia)
    3. discountActuallyApplied ¿Shopify lo está cobrando?     (sólo Shopify)

  El bug: (3) se leía de sessionStorage. Un flag local decía "aplicado" y la UI
  lo creía, mientras el carrito real decía otra cosa — o al revés. De ahí las
  cuatro caras del mismo fallo: el aviso "Beneficio aplicado" sobreviviendo a un
  carrito vacío, la oferta que no volvía tras retirar el código a mano, el modal
  que se abría para aplicar algo que ya estaba puesto, y el carrito "nuevo" que
  empezaba a 75.905.

  Ahora (3) sale SIEMPRE de `MasterCart.hasDiscountCode`, es decir de las
  aplicaciones que Shopify confirma. El almacenamiento local sólo hace dos
  cosas, y ninguna es económica: recordar que ya se enseñó, y dejar una pista de
  limpieza para un carrito vacío que aún arrastre el código.
*/
(() => {
  if (window.MasterExitSaveLoaded) return;
  window.MasterExitSaveLoaded = true;

  const rootUrl = window.MasterTheme?.root || '/';

  const store = api => ({
    get: key => { try { return window[api].getItem(key); } catch (_) { return null; } },
    set: (key, value) => { try { window[api].setItem(key, value); } catch (_) {} },
    remove: key => { try { window[api].removeItem(key); } catch (_) {} }
  });
  const session = store('sessionStorage');
  /* La pista de limpieza tiene que sobrevivir al cierre de pestaña: el código
     pegado a un carrito vacío también sobrevive. */
  const local = store('localStorage');

  const confirmedCart = () => window.MasterCart?.confirmed || null;

  const fetchCart = async () => {
    if (window.MasterCart?.getCart) return window.MasterCart.getCart({render: false});
    const response = await fetch(`${rootUrl}cart.js`, {headers: {Accept: 'application/json'}});
    if (!response.ok) throw new Error('No se pudo leer el carrito.');
    return response.json();
  };

  const applyDiscountCode = async code => {
    if (!window.MasterCart?.addDiscountCode) throw new Error('El carrito aún no está listo. Inténtalo de nuevo.');
    return window.MasterCart.addDiscountCode(code);
  };

  const setup = modal => {
    if (modal.dataset.masterReady === 'true') return;
    modal.dataset.masterReady = 'true';
    const overlay = document.querySelector(`[data-exit-save-overlay][data-owner="${modal.dataset.owner || ''}"]`) || document.querySelector('[data-exit-save-overlay]');
    if (!overlay) return;

    const trigger = modal.dataset.trigger || 'drawer';
    const once = modal.dataset.once === 'true';
    const delay = Math.max(0, Number(modal.dataset.delay || 0)) * 1000;
    const owner = modal.dataset.owner || 'global';
    const code = (modal.dataset.code || '').trim();
    const designMode = Boolean(window.Shopify?.designMode);
    const intents = new Set(String(modal.dataset.intents || 'close')
      .split(',').map(value => value.trim().toLowerCase()).filter(Boolean));

    const shownKey = `master-exit-save:shown:${location.host}:${owner}`;
    const attachedKey = `master-exit-save:attached:${location.host}:${code || owner}`;
    const started = Date.now();
    let previous = null;
    let applying = false;
    let lastApplied = false;
    let pending = null;   // intención original en espera de la decisión
    let resuming = false; // reejecución nuestra: no volver a interceptarla

    const status = modal.querySelector('[data-exit-save-status]');
    const accept = modal.querySelector('[data-exit-save-accept]');
    const defaultAcceptText = accept?.textContent || 'COMPLETAR MI PEDIDO';

    /* ---- Autoridad económica: Shopify, y sólo Shopify ------------------ */
    const appliedIn = cart => Boolean(code) && Boolean(window.MasterCart?.hasDiscountCode?.(cart, code));
    const hasItems = cart => Number(cart?.item_count || 0) > 0;

    /* ---- Frecuencia: local, y sólo frecuencia -------------------------- */
    const shown = () => !designMode && once && session.get(shownKey) === '1';
    const markShown = () => { if (!designMode && once) session.set(shownKey, '1'); };
    const rearm = () => session.remove(shownKey);
    const markAttached = () => { if (code) local.set(attachedKey, '1'); };

    /* ---- Contexto de compra: el formulario COD manda sobre el rescate ---- *
     *
     * El rescate nació cuando el único abandono posible era el del carrito.
     * Con contraentrega hay un segundo sitio donde el comprador puede irse —el
     * formulario de pedido del proveedor— y son contextos distintos, no el
     * mismo evento con otro nombre.
     *
     * Lo que MASTER hace aquí es abstenerse en tres situaciones donde ofrecer
     * algo sería acoso o mentira: encima del formulario abierto, en el rebote
     * inmediato de un cierre accidental, y después de un pedido ya hecho.
     * Quién sabe eso es MasterPurchase; si no está cargado, el rescate se
     * comporta exactamente como antes. */
    const purchaseAllowsRescue = () => {
      const context = window.MasterPurchase?.rescueContext?.();
      return context ? context.allowRescue : true;
    };

    const eligible = () => {
      if (!code) return false;
      if (!purchaseAllowsRescue()) return false;
      const cart = confirmedCart();
      if (!hasItems(cart)) return false;
      if (appliedIn(cart)) return false;
      if (shown()) return false;
      return Date.now() - started >= delay;
    };

    const setStatus = (message, kind = 'info') => {
      if (!status) return;
      status.textContent = message || '';
      status.dataset.kind = kind;
      status.hidden = !message;
    };

    const rescueId = `exit-save:${owner}`;

    const show = () => {
      if (!eligible()) return false;
      /* Otra superficie de rescate ya tiene la pantalla (p. ej. la
         recuperación al cerrar el formulario COD). Se cede en vez de abrirse
         encima: dos modales a la vez no son dos oportunidades. */
      if (!window.MasterPurchase?.claimRescue?.(rescueId)) return false;
      previous = document.activeElement;
      setStatus('');
      if (accept) { accept.disabled = false; accept.textContent = defaultAcceptText; }
      modal.hidden = false;
      overlay.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      // Bloqueo de scroll compartido con el drawer: en iOS `overflow:hidden`
      // sobre body no retiene el scroll, así que MasterScrollLock usa
      // position:fixed y restaura la posición al soltar.
      window.MasterScrollLock?.lock('exit-save-open');
      requestAnimationFrame(() => accept?.focus({preventScroll: true}));
      markShown();
      return true;
    };

    const hide = () => {
      if (modal.hidden) return;
      modal.setAttribute('aria-hidden', 'true');
      modal.hidden = true;
      overlay.hidden = true;
      window.MasterScrollLock?.unlock('exit-save-open');
      window.MasterPurchase?.releaseRescue?.(rescueId);
      previous?.focus?.({preventScroll: true});
    };

    /* Declinar significa exactamente lo que el comprador pidió antes de que le
       interrumpiéramos: cerrar, vaciar o irse. Nada más y nada menos. */
    const resumePending = () => {
      const job = pending;
      pending = null;
      if (!job) return;
      if (job.kind === 'close') {
        document.dispatchEvent(new CustomEvent('master:cart-force-close'));
        return;
      }
      if (job.kind === 'clear') {
        resuming = true;
        Promise.resolve(window.MasterCart?.clearCart?.())
          .catch(() => {})
          .finally(() => { resuming = false; });
        return;
      }
      if (job.kind === 'leave') {
        resuming = true;
        location.href = job.href;
        // Si la navegación no llega a ocurrir —un ancla interna, un
        // beforeunload que la cancela— el pestillo no puede quedarse echado:
        // dejaría la intercepción muerta el resto de la vida de la página.
        setTimeout(() => { resuming = false; }, 0);
      }
    };

    const decline = () => {
      if (applying) return;
      hide();
      resumePending();
    };

    /* Aceptar cancela la intención original: el comprador se queda. */
    const finishAccepted = () => {
      pending = null;
      if (modal.dataset.checkout === 'true') {
        location.href = `${rootUrl}checkout`;
        return;
      }
      hide();
      document.querySelector('[data-cart-open]')?.click();
    };

    const offerFor = job => {
      if (!intents.has(job.kind) || !eligible()) return false;
      pending = job;
      if (show()) return true;
      pending = null;
      return false;
    };

    const acceptOffer = async () => {
      if (applying) return;
      if (!code) { finishAccepted(); return; }
      applying = true;
      if (accept) { accept.disabled = true; accept.textContent = 'APLICANDO…'; }
      setStatus(modal.dataset.applyingText || 'Aplicando tu beneficio…', 'info');
      try {
        const before = await fetchCart();
        // Ya puesto: no se vuelve a pedir ni se finge un segundo descuento.
        if (appliedIn(before)) {
          markAttached();
          lastApplied = true;
          setStatus(modal.dataset.successText || '✓ Beneficio aplicado correctamente.', 'success');
          if (accept) accept.textContent = 'DESCUENTO APLICADO ✓';
          setTimeout(finishAccepted, 350);
          return;
        }
        // preserveDiscounts=false reemplaza los códigos existentes; por defecto
        // se suma al conjunto y Shopify decide la combinación.
        const preserve = modal.dataset.preserveDiscounts !== 'false';
        const after = preserve
          ? await applyDiscountCode(code)
          : await window.MasterCart.setDiscountCodes([code]);
        const outcome = window.MasterCart?.discountOutcome?.(before, after, code)
          || {applied: appliedIn(after)};
        if (!outcome.applied) {
          throw new Error(modal.dataset.errorText || 'El código no produjo descuento para este carrito.');
        }
        markAttached();
        lastApplied = true;
        setStatus(modal.dataset.successText || '✓ Beneficio aplicado correctamente.', 'success');
        if (accept) accept.textContent = 'DESCUENTO APLICADO ✓';
        document.dispatchEvent(new CustomEvent('master:rescue-discount-applied', {detail: {code, cart: after}}));
        setTimeout(finishAccepted, 650);
      } catch (error) {
        setStatus(error?.message || modal.dataset.errorText || 'No se pudo aplicar el beneficio.', 'error');
        if (accept) { accept.disabled = false; accept.textContent = 'INTENTAR DE NUEVO'; }
      } finally {
        applying = false;
      }
    };

    /* ---- Intenciones ---------------------------------------------------- */
    // Cerrar el drawer. Escape nunca se interrumpe —es la salida de emergencia
    // de un diálogo modal— y SEGUIR COMPRANDO tampoco: quien vuelve a mirar
    // producto no está abandonando nada.
    const onCloseRequest = event => {
      if (!['drawer', 'both'].includes(trigger)) return;
      const kind = event.detail?.trigger;
      if (kind === 'escape' || kind === 'continue') return;
      if (offerFor({kind: 'close'})) event.preventDefault();
    };

    // Vaciar y volver a la tienda se interceptan en fase de CAPTURA, antes de
    // que master-cart.js vea el clic: así la acción original no llega a
    // ejecutarse y puede reanudarse intacta si el comprador declina.
    const onCaptureClick = event => {
      if (resuming || !modal.hidden) return;
      const clearBtn = event.target.closest?.('[data-cart-clear]');
      if (clearBtn) {
        if (!offerFor({kind: 'clear'})) return;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const leaveLink = event.target.closest?.('[data-cart-leave]');
      if (leaveLink) {
        const href = leaveLink.getAttribute('href') || rootUrl;
        if (!offerFor({kind: 'leave', href})) return;
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const onMouseOut = event => {
      if (!['exit', 'both'].includes(trigger)) return;
      if (event.clientY <= 0 && !event.relatedTarget && eligible()) show();
    };

    const onClick = event => {
      if (event.target.closest('[data-exit-save-decline]')) { decline(); return; }
      if (event.target.closest('[data-exit-save-accept]')) acceptOffer();
    };
    const onKey = event => { if (event.key === 'Escape' && !modal.hidden && !applying) { event.preventDefault(); decline(); } };

    /* ---- Reconciliación ------------------------------------------------- */
    const syncFromCart = cart => {
      const applied = appliedIn(cart);
      if (applied) markAttached();
      // Retirada manual del código: la oferta vuelve a estar disponible en el
      // acto. Antes había que cerrar la pestaña para recuperarla, porque el
      // "ya aplicado" vivía en sessionStorage y nadie lo desmentía.
      if (lastApplied && !applied) rearm();
      lastApplied = applied;
    };

    const bootstrap = async () => {
      let cart = confirmedCart();
      if (!cart) { try { cart = await fetchCart(); } catch (_) { return; } }
      // Un carrito vacío puede seguir arrastrando el código sin enseñarlo: sin
      // líneas no hay asignación que leer. Si la pista dice que lo pegamos
      // nosotros, se suelta antes de que entre el siguiente producto.
      if (!hasItems(cart) && local.get(attachedKey) === '1') {
        try { cart = await window.MasterCart.detachSpecialFromEmptyCart() || cart; } catch (_) {}
        local.remove(attachedKey);
        rearm();
      }
      lastApplied = appliedIn(cart);
      if (lastApplied) markAttached();
    };

    document.addEventListener('master:cart-close-request', onCloseRequest);
    document.addEventListener('click', onCaptureClick, true);
    document.addEventListener('mouseout', onMouseOut);
    document.addEventListener('master:cart-updated', event => syncFromCart(event.detail?.cart || confirmedCart()));
    modal.addEventListener('click', onClick);
    overlay.addEventListener('click', decline);
    document.addEventListener('keydown', onKey);

    bootstrap();

    modal._masterCleanup = () => {
      document.removeEventListener('master:cart-close-request', onCloseRequest);
      document.removeEventListener('click', onCaptureClick, true);
      document.removeEventListener('mouseout', onMouseOut);
      document.removeEventListener('keydown', onKey);
      modal.removeEventListener('click', onClick);
      overlay.removeEventListener('click', decline);
    };
  };

  const init = scope => {
    const modals = scope.matches?.('[data-exit-save]') ? [scope] : [...scope.querySelectorAll?.('[data-exit-save]') || []];
    modals.forEach(setup);
  };

  init(document);
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => e.target.querySelectorAll?.('[data-exit-save]').forEach(el => el._masterCleanup?.()));
})();
